/**
 * 这里统一管理所有首页弹窗组件
 * 弹窗显示顺序
 * 1. 问卷弹窗 - 已下线
 * 2. 活动弹窗
 */

import React, { useState, useEffect } from 'react';
import ActivityPopup from '@/components/ActivityPopup';
import { PopupInfoResponse } from '$/api/server.types';
import { getPopupInfo } from '$/api/canvasRquest';
import localStorageUtil from '$/common/utils/localStorageUtil';
import { useOfficialStore } from '$/store/useOfficialStore';
import { mtstatTrack } from '$/utils/mtstat';

enum PopupName {
  Activity = 'ActivityPopup',
  Survey = 'SurveyPopup',
}

interface PopupInfo {
  type: PopupName;
  image: string;
  url: string;
  id?: string | number;
  name?: string;
  closeBtnPosition?: 'topRight' | 'bottomCenter';
}

const closeBtnPositionMap: Record<string, 'topRight' | 'bottomCenter'> = {
  right_top: 'topRight',
  middle_bottom: 'bottomCenter',
};
const HOME_POPUP_POSITION = 'home';

export default function HomePopups() {
  const userInfo = useOfficialStore((state) => state.userInfo);

  const [open, setOpen] = useState(false);
  const [popupList, setPopupList] = useState<PopupInfo[]>([]);
  const [currentPopupIndex, setCurrentPopupIndex] = useState(0);

  const currentPopup = popupList[currentPopupIndex] || {};

  const handlePopupClose = () => {
    // 如果是问卷弹窗，缓存用户id,下次不再显示
    if (currentPopup.type === PopupName.Survey) {
      localStorageUtil.setItem('surverPopupUid', userInfo.uid);
    }
    if (currentPopupIndex < popupList.length - 1) {
      setCurrentPopupIndex(currentPopupIndex + 1);
    } else {
      setOpen(false);
    }
  };

  // 关闭活动弹窗
  const handleActivityPopupClose = () => {
    if (currentPopup.type === PopupName.Activity) {
      mtstatTrack('home_banner_click', {
        id: currentPopup.id,
        name: currentPopup.name,
        click_type: 'close',
      });
    }

    handlePopupClose();
  };

  // 弹窗图片点击
  const handleActivityPopupImageClick = () => {
    if (currentPopup.type === PopupName.Activity) {
      mtstatTrack('home_banner_click', {
        id: currentPopup.id,
        name: currentPopup.name,
        click_type: 'click',
      });
    }
    if (currentPopup.url) {
      window.open(currentPopup.url, '_blank');
    }
    handlePopupClose();
  };

  // 是否显示活动弹窗
  const checkShowActivityPopup = (data: PopupInfoResponse) => {
    return data.popup.some((item) => item.position === HOME_POPUP_POSITION);
  };

  // 获取弹窗信息
  async function requestPopupInfo() {
    return getPopupInfo({ position: 'home' });
  }
  // 获取弹窗信息, 构造要显示的弹窗列表
  async function getPopupsInfo() {
    const res = await Promise.all([
      requestPopupInfo(),
    ]);
    const [popupInfo] = res;

    // 是否显示活动弹窗
    const showActivityPopup = checkShowActivityPopup(popupInfo);
    // 构造要显示的弹窗列表
    const newPopupList: PopupInfo[] = [];

    if (showActivityPopup) {
      popupInfo.popup
        .filter((item) => item.position === HOME_POPUP_POSITION)
        .forEach((item) => {
          newPopupList.push({
            id: item.id,
            name: item.title,
            type: PopupName.Activity,
            image: item.ext.img.url,
            url: item.ext.scheme,
            closeBtnPosition: closeBtnPositionMap[item.ext.close_btn.position],
          });
        });
    }

    if (newPopupList.length > 0) {
      setCurrentPopupIndex(0);
      setPopupList(newPopupList);
      setOpen(true);
    }
  }

  useEffect(() => {
    if (currentPopup.type === PopupName.Activity && open) {
      mtstatTrack('home_banner_exp', {
        id: currentPopup.id,
        name: currentPopup.name,
      });
    }
  }, [currentPopup.id, open]);

  useEffect(() => {
    if (userInfo.uid) {
      getPopupsInfo();
    }
  }, [userInfo.uid]);

  return (
    <div>
      <ActivityPopup
        open={open}
        image={currentPopup.image}
        closeBtnPosition={currentPopup.closeBtnPosition}
        onClose={handleActivityPopupClose}
        onImageClick={handleActivityPopupImageClick}
      />
    </div>
  );
}
